import { groupedByCoordinates, reducedToArray } from './SearchMap/SearchMap.helpers';

/**
 * Normalized key for grouping impact-experience listings by "same trip" title.
 * Uses publicData.experienceTitle when set; otherwise falls back to listing title (experiences only).
 * Stays use experienceTitle only (legacy behaviour when mixed with experiences at one pin).
 */
export const experienceTitleGroupKey = listing => {
  const pd = listing?.attributes?.publicData;
  const isExp = pd?.listingType === 'impact-experience';
  if (isExp) {
    const raw = (pd?.experienceTitle || listing?.attributes?.title || '').trim();
    return raw.slice(0, 48).toLowerCase() || 'undefined';
  }
  const raw = (pd?.experienceTitle || '').trim();
  return raw.slice(0, 48).toLowerCase() || 'undefined';
};

const sortExperienceGroup = group =>
  group.length === 1
    ? [group[0]]
    : group.sort((a, b) => {
        const aIndex = a?.attributes?.publicData?.reviewsSearchIndex || 0;
        const bIndex = b?.attributes?.publicData?.reviewsSearchIndex || 0;
        return bIndex - aIndex;
      });

/**
 * First pass: same as map search — per coordinate cluster, split experiences by title.
 */
export const reduceCoordinateGroupsToListingUnits = specificListings => {
  if (!specificListings) {
    return null;
  }
  return specificListings.reduce((acc, item) => {
    if (Array.isArray(item) && item.length > 0) {
      const hasImpactExperience = item.some(
        listing => listing?.attributes?.publicData?.listingType === 'impact-experience'
      );

      if (hasImpactExperience) {
        const groupedByTitle = {};

        item.forEach(subItem => {
          const k = experienceTitleGroupKey(subItem);
          if (!groupedByTitle[k]) {
            groupedByTitle[k] = [];
          }
          groupedByTitle[k].push(subItem);
        });

        const groupedArrays = Object.values(groupedByTitle).map(group =>
          group.length === 1 ? [group[0]] : sortExperienceGroup([...group])
        );
        return [...acc, ...groupedArrays];
      }
    }

    return [...acc, item];
  }, []);
};

/**
 * Merge impact-experience card groups that share the same title across different coordinates.
 */
export const mergeImpactExperienceGroupsAcrossCoordinates = groups => {
  if (!groups?.length) {
    return groups;
  }

  const buckets = new Map();
  const rest = [];

  for (const unit of groups) {
    if (!Array.isArray(unit) || unit.length === 0) {
      continue;
    }
    const allImpactExperience = unit.every(
      l => l?.attributes?.publicData?.listingType === 'impact-experience'
    );
    if (allImpactExperience) {
      for (const listing of unit) {
        const k = experienceTitleGroupKey(listing);
        if (!buckets.has(k)) {
          buckets.set(k, []);
        }
        buckets.get(k).push(listing);
      }
    } else {
      rest.push(unit);
    }
  }

  const mergedExperienceUnits = [];
  for (const arr of buckets.values()) {
    const byId = new Map();
    for (const l of arr) {
      const id = l?.id?.uuid;
      if (id) {
        byId.set(id, l);
      }
    }
    const deduped = [...byId.values()];
    mergedExperienceUnits.push(
      deduped.length === 1 ? [deduped[0]] : sortExperienceGroup(deduped)
    );
  }

  return [...mergedExperienceUnits, ...rest];
};

/**
 * Full pipeline: coordinate-aware reduce, then merge experiences with the same title across pins.
 */
export const buildSearchResultGroupsFromSpecificListings = specificListings => {
  if (!specificListings) {
    return null;
  }
  const afterCoords = reduceCoordinateGroupsToListingUnits(specificListings);
  return mergeImpactExperienceGroupsAcrossCoordinates(afterCoords);
};

/**
 * Calculate the displayed count of listings after grouping and filtering
 * This matches exactly how SearchResultsPanel processes listings
 */
export const getDisplayedListingsCount = (listings, nearbyLabelIndex = null, isDevEnv = false) => {
  if (!listings || listings.length === 0) {
    return 0;
  }

  // Filter out test listings in non-dev environments
  const filteredListings = isDevEnv
    ? listings
    : listings.filter(
        listing =>
          !(
            listing?.attributes?.publicData?.devListing &&
            listing?.attributes?.publicData?.devListing === true
          )
      );

  // Get listings that will actually be displayed (excluding nearby if shown separately)
  const listingsForDisplay =
    nearbyLabelIndex != null && nearbyLabelIndex > 0
      ? filteredListings.slice(0, nearbyLabelIndex)
      : filteredListings;

  // Group listings by coordinates (same as SearchResultsPanel does)
  const specificListings =
    nearbyLabelIndex !== null
      ? nearbyLabelIndex > 0
        ? reducedToArray(groupedByCoordinates(listingsForDisplay))
        : null
      : reducedToArray(groupedByCoordinates(listingsForDisplay));

  if (!specificListings) {
    return 0;
  }

  const processedListings = buildSearchResultGroupsFromSpecificListings(specificListings);

  return processedListings.length;
};
